import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";

// GradualBlur Component (inline) - FIXED VERSION
function GradualBlur({
  position = "top",
  height = "400px",
  strength = 40,
  divCount = 15,
  curve = "parabola",
  exponential = false,
}) {
  const CURVE_FUNCTIONS = {
    linear: (p) => p,
    bezier: (p) => p * p * (3 - 2 * p),
    "ease-in": (p) => p * p,
    "ease-out": (p) => 1 - Math.pow(1 - p, 2),
    "ease-in-out": (p) =>
      p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2,
    parabola: (p) => p * p * p,
  };

  const getGradientDirection = (pos) =>
    ({
      top: "to top",
      bottom: "to bottom",
      left: "to left",
      right: "to right",
    }[pos] || "to bottom");

  const blurDivs = useMemo(() => {
    const divs = [];
    const increment = 100 / divCount;
    const curveFunc = CURVE_FUNCTIONS[curve] || CURVE_FUNCTIONS.linear;

    for (let i = 1; i <= divCount; i++) {
      let progress = i / divCount;
      progress = curveFunc(progress);

      let blurValue;
      if (exponential) {
        blurValue = Math.pow(progress, 3) * strength * 2;
      } else {
        blurValue = progress * strength;
      }

      const p1 = Math.round((increment * i - increment) * 10) / 10;
      const p2 = Math.round(increment * i * 10) / 10;
      const p3 = Math.round((increment * i + increment) * 10) / 10;
      const p4 = Math.round((increment * i + increment * 2) * 10) / 10;

      let gradient = `transparent ${p1}%, black ${p2}%`;
      if (p3 <= 100) gradient += `, black ${p3}%`;
      if (p4 <= 100) gradient += `, transparent ${p4}%`;

      const direction = getGradientDirection(position);

      const divStyle = {
        position: "absolute",
        inset: "0",
        maskImage: `linear-gradient(${direction}, ${gradient})`,
        WebkitMaskImage: `linear-gradient(${direction}, ${gradient})`,
        backdropFilter: `blur(${blurValue.toFixed(2)}px)`,
        WebkitBackdropFilter: `blur(${blurValue.toFixed(2)}px)`,
      };

      divs.push(<div key={i} style={divStyle} />);
    }

    return divs;
  }, [position, strength, divCount, curve, exponential]);

  const containerStyle = {
    position: "fixed",
    pointerEvents: "none",
    zIndex: 40,
    height: height,
    width: "100%",
    [position]: 0,
    left: 0,
    right: 0,
  };

  return (
    <div style={containerStyle}>
      <div style={{ position: "relative", width: "100%", height: "100%" }}>
        {blurDivs}
      </div>
    </div>
  );
}

// ==================== LOGO COMPONENT ====================
const Logo = ({ mousePos, isMobile }) => {
  const ref = useRef(null);
  const pathRefs = useRef([]);
  const [logoSize, setLogoSize] = useState(105);

  useEffect(() => {
    const checkSize = () => {
      const width = window.innerWidth;
      const minSize = 42;
      const maxSize = 105;
      const minWidth = 375;
      const maxWidth = 1440;

      if (width <= minWidth) {
        setLogoSize(minSize);
      } else if (width >= maxWidth) {
        setLogoSize(maxSize);
      } else {
        const ratio = (width - minWidth) / (maxWidth - minWidth);
        const size = minSize + ratio * (maxSize - minSize);
        setLogoSize(Math.round(size));
      }
    };
    checkSize();
    window.addEventListener("resize", checkSize);
    return () => window.removeEventListener("resize", checkSize);
  }, []);

  useEffect(() => {
    if (isMobile || !ref.current) {
      pathRefs.current.forEach((path) => {
        if (path) path.setAttribute("stroke-width", "35");
      });
      return;
    }

    const rect = ref.current.getBoundingClientRect();
    const logoCenter = {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };

    const distance = Math.sqrt(
      Math.pow(mousePos.x - logoCenter.x, 2) +
        Math.pow(mousePos.y - logoCenter.y, 2)
    );

    const maxDistance = 100;
    const influence = Math.max(0, Math.min(1, 1 - distance / maxDistance));
    const width = 35 + influence * 10;

    pathRefs.current.forEach((path) => {
      if (path) path.setAttribute("stroke-width", width.toString());
    });
  }, [mousePos.x, mousePos.y, isMobile]);

  return (
    <Link to="/" ref={ref} className="block p-1">
      <svg width={logoSize} height={logoSize} viewBox="0 0 370.77 371.43">
        <path
          ref={(el) => (pathRefs.current[0] = el)}
          fill="none"
          stroke="#fff"
          strokeMiterlimit="10"
          strokeWidth="35"
          d="M141.36,266.46V116h62.18c20.48,0,34.09,16.6,34.09,37.09h0c0,20.21-13.27,37.76-33.48,38.14h-62.78,49.75l48,70.87"
          className="transition-[stroke-width] duration-150 ease-out"
        />
        <path
          ref={(el) => (pathRefs.current[1] = el)}
          fill="none"
          stroke="#fff"
          strokeMiterlimit="10"
          strokeWidth="35"
          d="M342.1,133.92c-21.65-66.04-83.79-113.74-157.08-113.74C93.74,20.19,19.75,94.18,19.75,185.46s73.99,165.27,165.27,165.27c86.02,0,161.21-66.98,165.27-159.5h-47.78"
          className="transition-[stroke-width] duration-150 ease-out"
        />
      </svg>
    </Link>
  );
};

// ==================== NAVLINK COMPONENT ====================
const NavLink = ({ to, children, mousePos, isMobile, isActive }) => {
  const ref = useRef(null);
  const letterRefs = useRef([]);

  useEffect(() => {
    if (isMobile) {
      const baseWeight = isActive ? 600 : 400;
      letterRefs.current.forEach((letterEl) => {
        if (letterEl) {
          letterEl.style.fontWeight = baseWeight.toString();
          letterEl.style.fontVariationSettings = `"wght" ${baseWeight}`;
        }
      });
      return;
    }

    if (!ref.current) return;

    const letters = ref.current.querySelectorAll(".nav-letter");
    letters.forEach((letter, i) => {
      const rect = letter.getBoundingClientRect();
      const letterCenter = {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      };

      const distance = Math.sqrt(
        Math.pow(mousePos.x - letterCenter.x, 2) +
          Math.pow(mousePos.y - letterCenter.y, 2)
      );

      const maxDistance = 100;
      const influence = Math.max(0, Math.min(1, 1 - distance / maxDistance));
      const weight = 400 + Math.round(influence * 500);

      if (letterRefs.current[i]) {
        letterRefs.current[i].style.fontWeight = weight.toString();
        letterRefs.current[i].style.fontVariationSettings = `"wght" ${weight}`;
      }
    });
  }, [mousePos.x, mousePos.y, isMobile, isActive]);

  return (
    <Link ref={ref} to={to} className="text-white no-underline">
      {children.split("").map((char, i) => (
        <span
          key={i}
          ref={(el) => (letterRefs.current[i] = el)}
          className="nav-letter inline-block transition-[font-weight] duration-150 ease-out"
          style={{
            fontWeight: 400,
            fontVariationSettings: '"wght" 400',
            minWidth: char === " " ? "0.3em" : "auto",
          }}
        >
          {char}
        </span>
      ))}
    </Link>
  );
};

// ==================== MAIN NAVIGATION COMPONENT ====================
export default function Navigation({ backgroundOverride }) {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);
  const [fluidFontSize, setFluidFontSize] = useState(24);
  const [fluidPaddingY, setFluidPaddingY] = useState(24);
  const [fluidPaddingX, setFluidPaddingX] = useState(75);
  const [fluidGap, setFluidGap] = useState(0);
  const [fluidMenuGap, setFluidMenuGap] = useState(0);
  const [fluidLetterSpacing, setFluidLetterSpacing] = useState("3px");
  const [fluidWidths, setFluidWidths] = useState({
    work: 123,
    renee: 129,
    contact: 147,
  });
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [blurHeight, setBlurHeight] = useState("300px");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const calculateFluidValues = () => {
      const width = window.innerWidth;
      const minWidth = 375;
      const maxWidth = 1440;

      const minFont = 20;
      const maxFont = 24;

      const minLetterSpacing = 1.5;
      const maxLetterSpacing = 3;

      const minWidths = { work: 85, renee: 90, contact: 105 };
      const maxWidths = { work: 123, renee: 129, contact: 147 };

      const minPadX = 24;
      const maxPadX = 75;

      const minGap = 12;
      const maxGap = 0;

      const minMenuGap = -5;
      const maxMenuGap = 0;

      if (width <= minWidth) {
        setFluidFontSize(minFont);
        setFluidPaddingY(16);
        setFluidPaddingX(minPadX);
        setFluidGap(minGap);
        setFluidMenuGap(minMenuGap);
        setFluidLetterSpacing(`${minLetterSpacing}px`);
        setFluidWidths(minWidths);
        setBlurHeight("275px");
        setIsMobile(true);
      } else if (width >= maxWidth) {
        setFluidFontSize(maxFont);
        setFluidPaddingY(24);
        setFluidPaddingX(maxPadX);
        setFluidGap(maxGap);
        setFluidMenuGap(maxMenuGap);
        setFluidLetterSpacing(`${maxLetterSpacing}px`);
        setFluidWidths(maxWidths);
        setBlurHeight("300px");
        setIsMobile(false);
      } else {
        const ratio = (width - minWidth) / (maxWidth - minWidth);

        const fontSize = minFont + ratio * (maxFont - minFont);
        setFluidFontSize(Math.round(fontSize));

        const minPadY = 16;
        const maxPadY = 24;
        const padY = minPadY + ratio * (maxPadY - minPadY);
        setFluidPaddingY(Math.round(padY));

        const padX = minPadX + ratio * (maxPadX - minPadX);
        setFluidPaddingX(Math.round(padX));

        const gap = minGap + ratio * (maxGap - minGap);
        setFluidGap(Math.round(gap));

        const menuGap = minMenuGap + ratio * (maxMenuGap - minMenuGap);
        setFluidMenuGap(Math.round(menuGap));

        const letterSpacing =
          minLetterSpacing + ratio * (maxLetterSpacing - minLetterSpacing);
        setFluidLetterSpacing(`${letterSpacing.toFixed(1)}px`);

        const widths = {
          work: Math.round(
            minWidths.work + ratio * (maxWidths.work - minWidths.work)
          ),
          renee: Math.round(
            minWidths.renee + ratio * (maxWidths.renee - minWidths.renee)
          ),
          contact: Math.round(
            minWidths.contact + ratio * (maxWidths.contact - minWidths.contact)
          ),
        };
        setFluidWidths(widths);

        const minBlur = 275;
        const maxBlur = 300;
        const blur = minBlur + ratio * (maxBlur - minBlur);
        setBlurHeight(`${Math.round(blur)}px`);

        setIsMobile(width < 768);
      }
    };

    calculateFluidValues();
    window.addEventListener("resize", calculateFluidValues);
    return () => window.removeEventListener("resize", calculateFluidValues);
  }, []);

  useEffect(() => {
    if (isMobile) return;

    let ticking = false;
    const handleMouseMove = (e) => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setMousePos({ x: e.clientX, y: e.clientY });
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [isMobile]);

  const defaultBackground = `
  linear-gradient(
    to bottom,
    rgba(0,0,0,0.7) 0%,
    rgba(0,0,0,0.5) 20%,
    rgba(0,0,0,0.3) 40%,
    rgba(0,0,0,0.15) 60%,
    rgba(0,0,0,0.05) 80%,
    rgba(0,0,0,0) 100%
  )
`;

  return (
    <>
      <GradualBlur
        position="top"
        height={blurHeight}
        strength={7}
        divCount={20}
        curve="parabola"
        exponential={false}
      />

      <nav
        className="fixed top-0 w-full z-50"
        style={{
          background: backgroundOverride || defaultBackground,
        }}
      >
        <div
          className="flex justify-between items-center"
          style={{
            padding: `${fluidPaddingY}px ${fluidPaddingX}px`,
            width: "100%",
            gap: `${fluidGap}px`,
          }}
        >
          <Logo mousePos={mousePos} isMobile={isMobile} />
          <div
            className="flex font-medium uppercase"
            style={{
              fontSize: `${fluidFontSize}px`,
              letterSpacing: fluidLetterSpacing,
              textShadow: "0 0 10px rgba(0, 0, 0, 0.4)",
              gap: `${fluidMenuGap}px`,
            }}
          >
            <div
              className="flex justify-center"
              style={{ width: `${fluidWidths.work}px` }}
            >
              <NavLink to="/work" mousePos={mousePos} isMobile={isMobile} isActive={location.pathname === '/work'}>
                WORK
              </NavLink>
            </div>
            <div
              className="flex justify-center"
              style={{ width: `${fluidWidths.renee}px` }}
            >
              <NavLink to="/renee" mousePos={mousePos} isMobile={isMobile} isActive={location.pathname === '/renee'}>
                RENÉE
              </NavLink>
            </div>
            <div
              className="flex justify-center"
              style={{
                width: `${fluidWidths.contact}px`,
                letterSpacing: "0px",
                fontFeatureSettings: "'kern' 1",
              }}
            >
              <NavLink to="/contact" mousePos={mousePos} isMobile={isMobile} isActive={location.pathname === '/contact'}>
                CONTACT
              </NavLink>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
}